import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import TrackCard from "@/components/TrackCard";
import { MagnifyingGlass, MusicNotes, Spinner, X, Archive as ArchiveIcon, YoutubeLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const SOURCE_PILLS = [
  { id: "all", label: "All" },
  { id: "youtube", label: "YouTube" },
  { id: "jamendo", label: "Jamendo" },
  { id: "archive", label: "Archive.org" },
];

// Curated "starter" search terms for YouTube — Indian-music heavy per user spec
const YT_PRESETS = [
  "Bollywood", "Tollywood", "Punjabi", "Tamil", "Telugu",
  "Bhojpuri", "Indian Classical", "Sufi", "Lofi India",
  "K-pop", "Latin", "Afrobeats", "J-pop",
];

export default function Music() {
  const { user } = useAuth();
  const { playTrack, current, mode, isPlaying } = usePlayer();

  const [sourceFilter, setSourceFilter] = useState("all");

  // Browse caches
  const [jamendoBrowse, setJamendoBrowse] = useState([]);
  const [archiveBrowse, setArchiveBrowse] = useState([]);
  const [ytBrowse, setYtBrowse] = useState([]);
  const [ytBrowseSeed, setYtBrowseSeed] = useState("Bollywood");

  // Chips
  const [genres, setGenres] = useState([]);
  const [collections, setCollections] = useState([]);
  const [activeTag, setActiveTag] = useState("");
  const [activeCollection, setActiveCollection] = useState("");

  // Search state
  const [searchInput, setSearchInput] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [searchJamendo, setSearchJamendo] = useState([]);
  const [searchArchive, setSearchArchive] = useState([]);
  const [searchYT, setSearchYT] = useState([]);

  // Archive item detail
  const [archiveOpen, setArchiveOpen] = useState(null);

  // Favorites (Jamendo/Archive only — YouTube uses video favorites which are separate)
  const [favIds, setFavIds] = useState(new Set());

  // Loading flags per-source
  const [loadingJamendo, setLoadingJamendo] = useState(false);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [loadingYT, setLoadingYT] = useState(false);
  const [loadingItem, setLoadingItem] = useState(false);

  const loadFavs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/favorites/tracks");
      setFavIds(new Set((data || []).map(t => t.track_id)));
    } catch (_) { /* ignore */ }
  }, [user]);
  useEffect(() => { loadFavs(); }, [loadFavs]);

  useEffect(() => {
    api.get("/jamendo/genres").then(r => setGenres(r.data || [])).catch(() => {});
    api.get("/archive/collections").then(r => setCollections(r.data || [])).catch(() => {});
  }, []);

  // Browse Jamendo
  const browseJamendo = useCallback(async (tag = "") => {
    setLoadingJamendo(true);
    try {
      const params = { limit: 36 };
      if (tag) params.tag = tag;
      const { data } = await api.get("/jamendo/tracks", { params });
      setJamendoBrowse(data.items || []);
    } catch (_) { /* silent */ }
    finally { setLoadingJamendo(false); }
  }, []);

  // Browse Archive
  const browseArchive = useCallback(async (collection = "") => {
    setLoadingArchive(true);
    try {
      const params = { rows: 30 };
      params.collection = collection || "audio_music";
      const { data } = await api.get("/archive/search", { params });
      setArchiveBrowse(data.items || []);
    } catch (_) { /* silent */ }
    finally { setLoadingArchive(false); }
  }, []);

  // Browse YouTube — seed with a preset query
  const browseYT = useCallback(async (seed) => {
    setLoadingYT(true);
    try {
      const { data } = await api.get("/youtube/search", { params: { q: seed, max_results: 24 } });
      setYtBrowse(data.items || []);
    } catch (_) { /* silent */ }
    finally { setLoadingYT(false); }
  }, []);

  // Run browse refresh when chips change
  useEffect(() => {
    if (sourceFilter === "all" || sourceFilter === "jamendo") browseJamendo(activeTag);
    if (sourceFilter === "all" || sourceFilter === "archive") browseArchive(activeCollection);
    if (sourceFilter === "all" || sourceFilter === "youtube") browseYT(ytBrowseSeed);
  }, [sourceFilter, activeTag, activeCollection, ytBrowseSeed, browseJamendo, browseArchive, browseYT]);

  // Run search across active sources
  const runSearch = useCallback(async (q, filter = sourceFilter) => {
    setActiveQuery(q);
    setActiveTag(""); setActiveCollection(""); setArchiveOpen(null);
    if (!q) {
      setSearchJamendo([]); setSearchArchive([]); setSearchYT([]);
      return;
    }
    const wantJ = filter === "all" || filter === "jamendo";
    const wantA = filter === "all" || filter === "archive";
    const wantY = filter === "all" || filter === "youtube";

    if (wantJ) {
      setLoadingJamendo(true);
      api.get("/jamendo/tracks", { params: { search: q, limit: 30 } })
        .then(r => setSearchJamendo(r.data.items || []))
        .catch(() => setSearchJamendo([]))
        .finally(() => setLoadingJamendo(false));
    } else setSearchJamendo([]);

    if (wantA) {
      setLoadingArchive(true);
      api.get("/archive/search", { params: { q, rows: 30 } })
        .then(r => setSearchArchive(r.data.items || []))
        .catch(() => setSearchArchive([]))
        .finally(() => setLoadingArchive(false));
    } else setSearchArchive([]);

    if (wantY) {
      setLoadingYT(true);
      api.get("/youtube/search", { params: { q, max_results: 24 } })
        .then(r => setSearchYT(r.data.items || []))
        .catch(() => setSearchYT([]))
        .finally(() => setLoadingYT(false));
    } else setSearchYT([]);
  }, [sourceFilter]);

  const onSubmit = (e) => { e.preventDefault(); runSearch(searchInput.trim()); };

  const openArchiveItem = async (identifier) => {
    setLoadingItem(true);
    try {
      const { data } = await api.get(`/archive/item/${identifier}`);
      setArchiveOpen(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (_) { toast.error("Couldn't open item"); }
    finally { setLoadingItem(false); }
  };

  const toggleFav = async (track) => {
    if (!user) { toast.error("Sign in to save tracks"); return; }
    const id = track.track_id;
    if (favIds.has(id)) {
      try {
        await api.delete(`/favorites/tracks/${encodeURIComponent(id)}`);
        const ns = new Set(favIds); ns.delete(id); setFavIds(ns);
      } catch (_) { toast.error("Failed"); }
    } else {
      try {
        await api.post("/favorites/tracks", track);
        const ns = new Set(favIds); ns.add(id); setFavIds(ns);
        toast.success("Saved");
      } catch (_) { toast.error("Failed"); }
    }
  };

  // YouTube track → dispatched internally by playTrack via source === 'youtube'
  const clearSearch = () => {
    setSearchInput(""); setActiveQuery("");
    setSearchJamendo([]); setSearchArchive([]); setSearchYT([]);
  };

  // Map a raw YT item to a "track" shape consumed by TrackCard
  const ytAsTrack = useCallback((it) => ({
    track_id: `youtube:${it.video_id}`,
    source: "youtube",
    title: it.title,
    artist: it.channel,
    album: "",
    artwork: it.thumbnail,
    stream_url: "",
    duration: 0,
    video_id: it.video_id,
  }), []);

  // Auto-skip when a YouTube video refuses to embed (error 150/101).
  // Walks down the visible list. Caps at 5 sequential failures to avoid infinite loops.
  const skipCountRef = useRef(0);
  const failedIdsRef = useRef(new Set());
  // Reset failed-video tracking only when the user changes list/search context.
  // Do not reset on optimistic `isPlaying` because the YT IFrame can still reject with 101/150.
  useEffect(() => { failedIdsRef.current = new Set(); skipCountRef.current = 0; }, [sourceFilter, activeQuery, ytBrowseSeed]);

  useEffect(() => {
    const handler = (e) => {
      const failed = e.detail?.video_id;
      if (!failed) return;
      failedIdsRef.current.add(failed);
      if (skipCountRef.current >= 5) {
        toast.error("Multiple tracks couldn't be embedded. Try a different search.");
        skipCountRef.current = 0;
        return;
      }
      const list = (activeQuery ? searchYT : ytBrowse);
      const idx = list.findIndex(it => it.video_id === failed);
      // Look for next non-failed track
      for (let i = idx + 1; i < list.length; i++) {
        if (!failedIdsRef.current.has(list[i].video_id)) {
          skipCountRef.current += 1;
          toast.message(`Skipping unembeddable track…`);
          playTrack(ytAsTrack(list[i]));
          return;
        }
      }
      // No more tracks
      toast.error("No more playable tracks in this list.");
    };
    window.addEventListener("recho-music:yt-embed-blocked", handler);
    return () => window.removeEventListener("recho-music:yt-embed-blocked", handler);
  }, [activeQuery, searchYT, ytBrowse, playTrack, ytAsTrack]);

  const playingId =
    isPlaying && current && mode === "track" ? current.track_id
    : isPlaying && current && mode === "yt-audio" ? `youtube:${current.video_id}`
    : null;

  const isSearching = Boolean(activeQuery);
  const showYT = sourceFilter === "all" || sourceFilter === "youtube";
  const showJamendo = sourceFilter === "all" || sourceFilter === "jamendo";
  const showArchive = sourceFilter === "all" || sourceFilter === "archive";

  const jamendoList = isSearching ? searchJamendo : jamendoBrowse;
  const archiveList = isSearching ? searchArchive : archiveBrowse;
  const ytList = (isSearching ? searchYT : ytBrowse).map(ytAsTrack);

  const totalJamendo = jamendoList.length;
  const totalArchive = archiveList.length;
  const totalYT = ytList.length;
  const anyResults = totalJamendo + totalArchive + totalYT > 0;
  const allLoading = (showYT && loadingYT) || (showJamendo && loadingJamendo) || (showArchive && loadingArchive);

  const stateContextLabel = useMemo(() => {
    if (isSearching) return `“${activeQuery}”`;
    if (activeTag) return `Jamendo · ${activeTag}`;
    if (activeCollection) return `Archive · ${activeCollection}`;
    if (showYT && !showJamendo && !showArchive) return `YouTube · ${ytBrowseSeed}`;
    return "Popular today";
  }, [isSearching, activeQuery, activeTag, activeCollection, showYT, showJamendo, showArchive, ytBrowseSeed]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-12 pb-32 sm:pb-40">
      {/* Header */}
      <div className="mb-5 sm:mb-10 border-b border-[#1F2924]/20 pb-4 sm:pb-6">
        <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D35230] mb-2 flex items-center gap-2">
          <MusicNotes size={14} weight="fill" /> Music atlas
        </div>
        <h1 className="font-serif-display text-3xl sm:text-5xl lg:text-6xl leading-tight">Free music</h1>
        <p className="text-sm sm:text-base text-[#1F2924]/70 mt-2 sm:mt-3 max-w-2xl">
          Search Bollywood, Tollywood, world charts &amp; more across YouTube, Jamendo and the Internet Archive.
        </p>
      </div>

      {/* Search bar */}
      <form onSubmit={onSubmit} className="flex items-center gap-2 mb-4">
        <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 focus-within:border-[#D35230]">
          <MagnifyingGlass size={16} color="#4A5A50" />
          <input
            data-testid="music-search-input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search Bollywood, Tollywood, artist, album…"
            className="flex-1 min-w-0 bg-transparent outline-none font-sans text-sm sm:text-base"
          />
          {(searchInput || activeQuery) && (
            <button
              type="button"
              onClick={clearSearch}
              aria-label="Clear search"
              data-testid="music-search-clear"
              className="w-6 h-6 text-[#4A5A50] hover:text-[#D35230] flex items-center justify-center shrink-0"
            ><X size={14} /></button>
          )}
        </div>
        <button
          data-testid="music-search-button"
          type="submit"
          className="px-4 sm:px-5 min-h-[44px] py-2.5 border-2 border-[#1F2924] bg-[#1F2924] text-[#F4F1EA] hover:bg-[#D35230] hover:border-[#D35230] font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] shrink-0"
        >
          Search
        </button>
      </form>

      {/* Source filter pills */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mr-1">Source:</span>
        {SOURCE_PILLS.map(p => (
          <button
            key={p.id}
            data-testid={`music-source-${p.id}`}
            onClick={() => {
              setSourceFilter(p.id);
              if (activeQuery) runSearch(activeQuery, p.id);
            }}
            className={`px-3 py-1.5 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] border-2 transition-colors ${
              sourceFilter === p.id
                ? "bg-[#1F2924] border-[#1F2924] text-[#F4F1EA]"
                : "border-[#1F2924]/30 hover:border-[#1F2924]"
            }`}
          >{p.label}</button>
        ))}
      </div>

      {/* Browse chips */}
      {!isSearching && (
        <div className="flex gap-2 overflow-x-auto pb-3 mb-5 -mx-4 sm:mx-0 px-4 sm:px-0 scrollbar-thin">
          {showYT && (
            <>
              {YT_PRESETS.map(seed => (
                <Chip key={seed} active={ytBrowseSeed === seed} onClick={() => setYtBrowseSeed(seed)}>{seed}</Chip>
              ))}
              {(showJamendo || showArchive) && <span className="w-px bg-[#1F2924]/20 mx-2 shrink-0" />}
            </>
          )}
          {showJamendo && (
            <>
              <Chip active={activeTag === ""} onClick={() => setActiveTag("")}>Popular</Chip>
              {genres.slice(0, 12).map(g => (
                <Chip key={g.name} active={activeTag === g.name} onClick={() => setActiveTag(g.name)}>{g.label}</Chip>
              ))}
              {showArchive && <span className="w-px bg-[#1F2924]/20 mx-2 shrink-0" />}
            </>
          )}
          {showArchive && (
            <>
              <Chip active={activeCollection === ""} onClick={() => setActiveCollection("")}>All archive</Chip>
              {collections.slice(0, 6).map(c => (
                <Chip key={c.id} active={activeCollection === c.id} onClick={() => setActiveCollection(c.id)}>{c.label}</Chip>
              ))}
            </>
          )}
        </div>
      )}

      {/* Body */}
      {archiveOpen ? (
        <ArchiveItemDetail
          item={archiveOpen}
          onBack={() => setArchiveOpen(null)}
          playTrack={playTrack}
          toggleFav={toggleFav}
          favIds={favIds}
          playingId={playingId}
        />
      ) : loadingItem ? (
        <LoadingState label="Loading item…" />
      ) : allLoading && !anyResults ? (
        <LoadingState label={`Searching ${stateContextLabel}…`} />
      ) : !anyResults && !allLoading ? (
        <EmptyState query={activeQuery} onReset={clearSearch} />
      ) : (
        <div className="space-y-8 sm:space-y-10">
          {showYT && (
            <ResultSection
              title="YouTube · Bollywood, Tollywood &amp; more"
              subtitle={isSearching ? "Tracks" : `Browsing · ${ytBrowseSeed}`}
              count={totalYT}
              loading={loadingYT}
              icon={<YoutubeLogo size={16} weight="fill" color="#B91C1C" />}
              testId="music-section-youtube"
            >
              {ytList.length === 0 && !loadingYT ? (
                <SectionEmpty source="youtube" />
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                  {ytList.map(t => (
                    <TrackCard
                      key={t.track_id}
                      track={t}
                      onPlay={playTrack}
                      onFav={toggleFav}
                      isFav={favIds.has(t.track_id)}
                      isPlaying={playingId === t.track_id}
                    />
                  ))}
                </div>
              )}
            </ResultSection>
          )}

          {showJamendo && (
            <ResultSection
              title="Jamendo · Creative Commons"
              subtitle={isSearching ? "Tracks" : "Tracks · Popular"}
              count={totalJamendo}
              loading={loadingJamendo}
              testId="music-section-jamendo"
            >
              {jamendoList.length === 0 && !loadingJamendo ? (
                <SectionEmpty source="jamendo" />
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
                  {jamendoList.map(t => (
                    <TrackCard
                      key={t.track_id}
                      track={t}
                      onPlay={playTrack}
                      onFav={toggleFav}
                      isFav={favIds.has(t.track_id)}
                      isPlaying={playingId === t.track_id}
                    />
                  ))}
                </div>
              )}
            </ResultSection>
          )}

          {showArchive && (
            <ResultSection
              title="Internet Archive · Public domain"
              subtitle={isSearching ? "Albums / Items" : "Items · Collection"}
              count={totalArchive}
              loading={loadingArchive}
              testId="music-section-archive"
            >
              {archiveList.length === 0 && !loadingArchive ? (
                <SectionEmpty source="archive" />
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-3">
                  {archiveList.map(it => (
                    <button
                      key={it.identifier}
                      data-testid="archive-item-card"
                      onClick={() => openArchiveItem(it.identifier)}
                      className="text-left bg-[#EAE4D3] border-2 border-[#1F2924]/15 hover:border-[#1F2924] overflow-hidden transition-colors group min-w-0"
                    >
                      <div className="aspect-square bg-[#1F2924] overflow-hidden">
                        <img
                          src={it.artwork}
                          alt=""
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-2 sm:p-3 min-w-0">
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#3C5A41] mb-1 flex items-center gap-1">
                          <ArchiveIcon size={10} /> {it.year || "—"}
                        </div>
                        <div className="font-serif-display text-sm sm:text-base leading-tight line-clamp-2 min-h-[2.5rem] break-words" title={it.title}>
                          {it.title}
                        </div>
                        <div className="font-mono text-[9px] text-[#4A5A50] mt-1 truncate uppercase tracking-wider" title={it.creator || "anon"}>
                          {it.creator || "anon"}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </ResultSection>
          )}
        </div>
      )}
    </div>
  );
}

/* ============ presentational subcomponents ============ */

function Chip({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 px-3 py-1.5 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] border-2 transition-colors ${
        active
          ? "bg-[#D35230] border-[#D35230] text-[#F4F1EA]"
          : "border-[#1F2924]/30 hover:border-[#1F2924]"
      }`}
    >{children}</button>
  );
}

function ResultSection({ title, subtitle, count, loading, testId, icon, children }) {
  return (
    <section data-testid={testId}>
      <div className="flex items-end justify-between mb-3 sm:mb-4 gap-3 border-b border-[#1F2924]/15 pb-2">
        <div className="min-w-0 flex items-start gap-2">
          {icon && <div className="mt-1 shrink-0">{icon}</div>}
          <div className="min-w-0">
            <h2 className="font-serif-display text-xl sm:text-2xl leading-tight">{title}</h2>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A5A50] mt-0.5">
              {subtitle} {count > 0 && `· ${count}`}
            </div>
          </div>
        </div>
        {loading && <Spinner size={16} className="animate-spin shrink-0" color="#D35230" />}
      </div>
      {children}
    </section>
  );
}

function LoadingState({ label }) {
  return (
    <div data-testid="music-loading" className="flex flex-col items-center gap-3 py-16 sm:py-24">
      <Spinner size={32} className="animate-spin" color="#D35230" />
      <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#D35230]">{label}</div>
    </div>
  );
}

function EmptyState({ query, onReset }) {
  return (
    <div data-testid="music-empty" className="text-center py-16 sm:py-24">
      <MusicNotes size={48} color="#D35230" weight="duotone" className="mx-auto mb-4" />
      <div className="font-serif-display text-2xl sm:text-3xl text-[#4A5A50] mb-2">
        {query ? "No results found" : "Nothing here yet"}
      </div>
      <div className="font-sans text-sm text-[#1F2924]/60 mb-5">
        {query
          ? <>We searched YouTube, Jamendo and Internet Archive for <span className="italic">&quot;{query}&quot;</span> — try a different keyword or source.</>
          : "Pick a category or search for an artist."}
      </div>
      {query && (
        <button
          onClick={onReset}
          className="px-4 py-2 border-2 border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA] font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em]"
        >Reset search</button>
      )}
    </div>
  );
}

function SectionEmpty({ source }) {
  const labels = { youtube: "YouTube", jamendo: "Jamendo", archive: "Internet Archive" };
  return (
    <div className="bg-[#EAE4D3]/60 border border-dashed border-[#1F2924]/30 py-8 sm:py-10 text-center">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#4A5A50]">
        No results from {labels[source] || source}
      </div>
    </div>
  );
}

function ArchiveItemDetail({ item, onBack, playTrack, toggleFav, favIds, playingId }) {
  return (
    <div>
      <button
        data-testid="archive-item-back"
        onClick={onBack}
        className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] mb-4 hover:text-[#D35230] flex items-center gap-1"
      >
        ← Back to results
      </button>
      <div className="flex flex-col sm:flex-row gap-4 sm:gap-5 mb-5 sm:mb-6 pb-5 border-b border-[#1F2924]/20">
        <img src={item.artwork} alt="" className="w-28 h-28 sm:w-40 sm:h-40 object-cover border-2 border-[#1F2924] shrink-0" loading="lazy" />
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#3C5A41] mb-1">Archive · Item</div>
          <h2 className="font-serif-display text-2xl sm:text-4xl leading-tight break-words">{item.title}</h2>
          <div className="font-sans text-sm text-[#4A5A50] mt-1 truncate">{item.creator}</div>
          {item.description && <p className="text-sm text-[#1F2924]/70 mt-3 line-clamp-4">{item.description}</p>}
        </div>
      </div>
      {item.tracks.length === 0 ? (
        <div className="text-center py-10 sm:py-16">
          <MusicNotes size={36} color="#D35230" weight="duotone" className="mx-auto mb-3" />
          <div className="font-serif-display text-xl text-[#4A5A50]">No MP3 tracks in this item.</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3">
          {item.tracks.map(t => (
            <TrackCard
              key={t.track_id}
              track={t}
              onPlay={playTrack}
              onFav={toggleFav}
              isFav={favIds.has(t.track_id)}
              isPlaying={playingId === t.track_id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
